/**
 * Graded price check — live eBay ACTIVE ASKS for a slab, lowest first, via the
 * ebay-browse Edge Function (production Browse API, free app token).
 *
 * Honest framing everywhere: these are ASKING prices, not sold prices. Asks
 * are the market's ceiling; the lowest ask on a liquid card is a useful
 * pricing signal, and real sold comps stay one click away (eBay Solds).
 * "Use" applies a per-unit price to the lot: Current FMV (total), Unrealized
 * G/L recomputed, FMV Locked (manual semantics — the nightly sweep skips
 * graded lots anyway), Valuation Source "eBay Active Asks", + an fmv_history
 * snapshot so the drawer chart records the change.
 */
import { DB } from "../lib/db";
import { sb } from "../lib/supabase";
import { escapeHtml, toast } from "../lib/dom";
import { openModal, closeModal } from "./modal";
import { render } from "../state";
import { fmt$ } from "../utils/format";

interface Ask { title: string; price: number; shipping: number; condition: string; url: string; image: string }

let _ctx: { lotId: string } | null = null;
let _items: Ask[] = [];

export function openGradedPriceCheck(lotId: string): void {
  const row = DB.table("cards").find(x => x["Card Lot ID"] === lotId);
  if (!row) { toast("Card lot not found", "err"); return; }
  _ctx = { lotId };
  const condGrade = String(row["Condition/Grade"] || "");
  const gm = condGrade.match(/^([A-Za-z]+)\s+([\d.]+|Authentic|Pending)/);
  const num = String(row["Card #"] || "").split("/")[0];
  const q = [
    gm?.[1] || "", gm?.[2] || "",
    String(row["Card Name"] || ""),
    num ? "#" + num : "",
    String(row["Set"] || ""),
  ].filter(Boolean).join(" ");

  const body = `
    <div class="space-y-3">
      <div class="flex gap-2">
        <input class="input flex-1" id="gpc_q" value="${escapeHtml(q)}" placeholder="e.g. PSA 10 Charizard ex #199">
        <button class="btn btn-ghost" onclick="_gpcSearch()">Search</button>
      </div>
      <div class="text-xs text-[var(--muted)]">Live eBay <b>asking</b> prices (not solds), lowest first. For real sold comps use the eBay Solds button on the lot.</div>
      <div id="gpc_stats" class="text-sm"></div>
      <div id="gpc_results" class="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
        <div class="text-sm text-[var(--muted)] italic py-3 text-center">Searching…</div>
      </div>
    </div>`;
  openModal({
    title: `Graded price check — ${escapeHtml(lotId)}`,
    body,
    footer: `<button class="btn btn-ghost" onclick="closeModal()">Close</button>`,
    wide: true,
  });
  void gpcSearch();
}

async function gpcSearch(): Promise<void> {
  const out = document.getElementById("gpc_results");
  const stats = document.getElementById("gpc_stats");
  const q = (document.getElementById("gpc_q") as HTMLInputElement | null)?.value.trim() || "";
  if (!q) { toast("Type a search first", "err"); return; }
  if (out) out.innerHTML = `<div class="text-sm text-[var(--muted)] italic py-3 text-center">Searching eBay…</div>`;
  if (stats) stats.textContent = "";
  try {
    const { data, error } = await sb.functions.invoke("ebay-browse", { body: { action: "search", q, limit: 12 } });
    let payload: any = data;
    if (error) {
      try { payload = await (error as any).context?.json?.(); } catch { /* keep */ }
      if (!payload) throw error;
    }
    if (payload?.error) throw new Error(payload.error + (payload.body ? " — " + String(payload.body).slice(0, 200) : ""));
    _items = (payload?.items || []) as Ask[];
    if (!_items.length) {
      if (out) out.innerHTML = `<div class="text-sm text-[var(--muted)] italic py-3 text-center">No active listings matched — loosen the search.</div>`;
      return;
    }
    const prices = _items.map(x => x.price).sort((a, b) => a - b);
    const low = prices[0]!;
    const median = prices.length % 2
      ? prices[(prices.length - 1) / 2]!
      : +(((prices[prices.length / 2 - 1]! + prices[prices.length / 2]!) / 2).toFixed(2));
    if (stats) stats.innerHTML =
      `<b>${_items.length}</b> asks · low <b class="stat-num">${fmt$(low)}</b> · median <b class="stat-num">${fmt$(median)}</b>` +
      ` <button class="btn btn-ghost !py-0.5 !px-2 text-xs ml-2" onclick="_gpcUsePrice(${median})" title="Set this lot's unit FMV to the median ask">Use median</button>`;
    if (out) out.innerHTML = _items.map((it, i) => `
      <div class="panel p-2 flex items-center gap-2">
        ${it.image ? `<img src="${escapeHtml(it.image)}" class="rounded border border-[var(--border)]" style="height:44px;width:auto" alt="">` : ""}
        <div class="min-w-0 flex-1">
          <a class="text-sm text-cyan-300 hover:underline" href="${escapeHtml(it.url)}" target="_blank" rel="noopener" title="${escapeHtml(it.title)}">${escapeHtml(it.title.slice(0, 90))}</a>
          <div class="text-xs text-[var(--muted)]">${escapeHtml(it.condition)}${it.shipping ? ` · +${fmt$(it.shipping)} ship` : " · free ship"}</div>
        </div>
        <b class="stat-num whitespace-nowrap">${fmt$(it.price)}</b>
        <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="_gpcUse(${i})" title="Set this lot's unit FMV to this ask">Use</button>
      </div>`).join("");
  } catch (e: any) {
    if (out) out.innerHTML = `<div class="text-sm text-rose-300 py-3 text-center">${escapeHtml(e?.message || String(e))}</div>`;
  }
}

function applyUnitPrice(unit: number): void {
  if (!_ctx || !(unit > 0)) return;
  const row = DB.table("cards").find(x => x["Card Lot ID"] === _ctx!.lotId);
  if (!row) return;
  const qty = Number(row["Qty"]) || 1;
  const total = +(unit * qty).toFixed(2);
  const totalBasis = Number(row["Total Basis"]) || 0;
  DB.update("cards", "Card Lot ID", _ctx.lotId, {
    "Current FMV": total,
    "Unrealized G/L": +(total - totalBasis).toFixed(2),
    "FMV Locked": true,
    "Valuation Source": "eBay Active Asks",
  });
  void DB.add("fmvhistory", {
    "Lot ID": _ctx.lotId,
    "Kind": "card",
    "Recorded At": new Date().toISOString(),
    "FMV": total,
    "Qty": qty,
    "Source": "eBay active asks",
  });
  toast(`FMV set to ${fmt$(unit)}/unit (locked) — based on active asks`);
  closeModal();
  render();
}

// Inline-handler bridges
(window as any).openGradedPriceCheck = openGradedPriceCheck;
(window as any)._gpcSearch   = () => { void gpcSearch(); };
(window as any)._gpcUse      = (i: number) => { const it = _items[i]; if (it) applyUnitPrice(it.price); };
(window as any)._gpcUsePrice = (p: number) => { applyUnitPrice(p); };
